import { createReducer } from "@reduxjs/toolkit";
import moment from "moment";
import * as actionTypes from "~/store/action_types";

const subtasks = createReducer({}, (builder) => {
  builder
    .addCase(actionTypes.ADD_SUBTASK_LOADING, (state, action) => {
      return {
        ...state,
        [action.id]: {
          id: action.id,
          name: action.attributes.body,
          task_id: action.taskId,
          project_id: action.projectId,
          assignee_id: action.attributes.assignee_id,
          is_completed: false,
          is_trashed: false,
          pending: true,
          position: Object.keys(state)?.length + 1,
        },
      };
    })
    .addCase(actionTypes.ADD_SUBTASK_FROM_EVENT_STREAM, (state, action) => {
      const { response = { entities: {} } } = action;
      const { entities = { subtasks: {} } } = response;
      const { subtasks = {} } = entities;

      const subtaskArray = Object.values(state);

      //skip update state if any fake subtask is in loading (realtime is faster then response)
      if (subtaskArray.find((subtask) => subtask.pending && typeof subtask.id === "string")) {
        return state;
      }

      return { ...state, ...subtasks };
    })
    .addCase(actionTypes.ADD_SUBTASK_SUCCESS, (state, action) => {
      const { response = { entities: {} } } = action;
      const { entities = { subtasks: {} } } = response;
      const { subtasks = {} } = entities;

      const newState = { ...state, ...subtasks };
      if (action.id && state[action.id]) {
        delete newState[action.id];
      }

      return newState;
    })
    .addCase(actionTypes.FETCH_TEAM_TASKS_SUCCESS, (state, action) => {
      const { response } = action;
      const { result = {} } = response;
      const { subtasks = {} } = result;

      Object.values(subtasks).forEach((subtask) => {
        state = { ...state, [subtask.id]: { ...subtask } };
      });
      return state;
    })
    .addCase(actionTypes.REORDER_SUBTASKS_SUCCESS, (state, action) => {
      const { response = [] } = action;
      response.forEach((id, index) => {
        const subtask = state[id];
        if (subtask) {
          state = { ...state, [id]: { ...subtask, position: index + 1 } };
        }
      });
      return state;
    })
    .addCase(actionTypes.RESTORE_SUBTASK_FROM_TRASH_SUCCESS, (state, action) => {
      if (state[action.id]) {
        state = {
          ...state,
          [action.id]: {
            ...state[action.id],
            is_trashed: false,
            pending: false,
          },
        };
      }
      return state;
    })
    .addCase(actionTypes.OPEN_TASK_LOADING, (state, action) => {
      const { taskId = 0, response = {}, completedOn = 0 } = action;
      const { result: realtimeTaskId = 0 } = response;

      const id = taskId > 0 ? taskId : realtimeTaskId;
      const toUpdated = Object.values(state).filter((subtask) => {
        return subtask.task_id === id && subtask.completed_on >= completedOn;
      });

      toUpdated.forEach((subtask) => {
        state = {
          ...state,
          [subtask.id]: {
            ...subtask,
            is_completed: false,
            completed_on: null,
          },
        };
      });
      return state;
    })
    .addCase(actionTypes.RESCHEDULE_TASK_LOADING, (state, action) => {
      const { taskId = 0 } = action;
      if (taskId !== 0) {
        const toUpdated = Object.values(state).filter((subtask) => {
          return subtask.task_id === taskId && subtask?.due_on !== null;
        });
        toUpdated.forEach((subtask) => {
          state = {
            ...state,
            [subtask.id]: {
              ...subtask,
              loading_due_date_reschedule: true,
            },
          };
        });
        return state;
      }
      return state;
    })
    .addCase(actionTypes.RESCHEDULE_TASK_ERROR, (state, action) => {
      const { taskId = 0 } = action;
      if (taskId !== 0) {
        const toUpdated = Object.values(state).filter((subtask) => {
          return subtask.task_id === taskId && subtask?.due_on !== null;
        });
        toUpdated.forEach((subtask) => {
          state = {
            ...state,
            [subtask.id]: {
              ...subtask,
              loading_due_date_reschedule: false,
            },
          };
        });
        return state;
      }
      return state;
    })
    .addCase(actionTypes.BATCH_ADD_SUBTASKS_SUCCESS, (state, action) => {
      const { response = {} } = action;
      const { entities } = response;
      const { subtasks = {} } = entities;

      return { ...state, ...subtasks };
    })
    .addMatcher(
      (action) =>
        [
          actionTypes.MOVE_USER_TO_ARCHIVE_FROM_EVENT_STREAM_SUCCESS,
          actionTypes.USER_MOVE_TO_TRASH_FROM_EVENT_STREAM_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = {} } = action;
        const { result: id } = response;

        const subtasks = state;
        Object.keys(subtasks).forEach((key) => {
          if (subtasks[key]?.assignee_id === id) {
            subtasks[key].assignee_id = 0;
          }
        });
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.ADD_SUBTASK_ERROR,
          actionTypes.DELETE_SUBTASK_SUCCESS,
          actionTypes.CONVERT_SUBTASK_TO_TASK_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { [action.id]: subtask, ...rest } = state;
        state = rest;
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.COMPLETE_SUBTASK_SUCCESS,
          actionTypes.OPEN_SUBTASK_SUCCESS,
          actionTypes.EDIT_SUBTASK_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = { entities: {} }, id } = action;
        const { entities = { subtasks: {} } } = response;
        const { subtasks = {} } = entities;

        const subtask = subtasks[id];
        if (subtask) {
          state = { ...state, [id]: subtask };
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.CONVERT_SUBTASK_TO_TASK_LOADING,
          actionTypes.COMPLETE_SUBTASK_LOADING,
          actionTypes.OPEN_SUBTASK_LOADING,
          actionTypes.EDIT_SUBTASK_LOADING,
          actionTypes.MOVE_SUBTASK_TO_TRASH_LOADING,
        ].includes(action.type),
      (state, action) => {
        const prev = state[action.id];
        if (prev) {
          if (action.attributes && action.attributes?.instant_change) {
            const dueOn =
              action.attributes.due_on && typeof action.attributes.due_on === "string"
                ? moment.utc(action.attributes.due_on, "YYYY-MM-DD").unix()
                : prev.due_on;
            state = {
              ...state,
              [action.id]: {
                ...prev,
                ...action.attributes,
                pending: true,
                due_on: dueOn,
              },
            };
          } else {
            state = { ...state, [action.id]: { ...prev, pending: true } };
          }
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.CONVERT_SUBTASK_TO_TASK_ERROR,
          actionTypes.COMPLETE_SUBTASK_ERROR,
          actionTypes.OPEN_SUBTASK_ERROR,
          actionTypes.EDIT_SUBTASK_ERROR,
          actionTypes.MOVE_SUBTASK_TO_TRASH_ERROR,
        ].includes(action.type),
      (state, action) => {
        const prev = state[action.id];
        if (prev) {
          if (action._previous) {
            return {
              ...state,
              [action.id]: {
                ...action._previous,
                pending: false,
              },
            };
          } else {
            return { ...state, [action.id]: { ...prev, pending: false } };
          }
        }
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [actionTypes.COMPLETE_TASK_FROM_EVENT_STREAM, actionTypes.COMPLETE_TASK_LOADING].includes(action.type),
      (state, action) => {
        const { taskId = 0, response = {} } = action;
        const { result: realtimeTaskId = 0 } = response;

        const id = taskId > 0 ? taskId : realtimeTaskId;
        const toUpdated = Object.values(state).filter((subtask) => {
          return subtask.task_id === id;
        });

        toUpdated.forEach((subtask) => {
          state[subtask.id] = {
            ...subtask,
            is_completed: true,
            completed_on: moment().unix(),
          };
        });
        return state;
      }
    )
    .addMatcher(
      (action) =>
        [
          actionTypes.MOVE_SUBTASK_TO_TRASH_SUCCESS,
          actionTypes.FETCH_ACTIVITY_LOGS_SUCCESS,
          actionTypes.FETCH_SUBTASKS_SUCCESS,
          actionTypes.FETCH_WORKLOAD_SUBTASKS_SUCCESS,
          actionTypes.LOAD_TASK,
          actionTypes.COMPLETE_TASK_SUCCESS,
          actionTypes.OPEN_TASK_SUCCESS,
          actionTypes.ANGULAR_INIT_TASK_AND_COMMENTS_MODAL,
          actionTypes.FETCH_USER_TASKS_SUCCESS,
          actionTypes.FETCH_SUBTASKS_TIMELINE_SUCCESS,
          actionTypes.FETCH_CALENDAR_ASSIGMENTS_SUCCESS,
          actionTypes.PROJECT_TEMPLATE_APPLY_SUCCESS,
        ].includes(action.type),
      (state, action) => {
        const { response = { entities: {} } } = action;
        const { entities = { subtasks: {} } } = response;
        const { subtasks = {} } = entities;

        return { ...state, ...subtasks };
      }
    );
});

export default subtasks;
